import { useEffect } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigationType } from "react-router-dom";
import logoUrl from "@assets/logo.svg?url";
import { applyMeta, pageFor } from "../lib/routes.ts";
import { Wrap } from "./ui/index.ts";

/**
 * Keep the head describing the page you are actually on.
 *
 * A cold load already arrives with the right title, description and canonical
 * baked into the file, so on that first page this changes nothing. It is for
 * every navigation after it, which swaps the page out from under a head that
 * still describes the last one.
 */
function usePageMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    applyMeta(pageFor(pathname));
  }, [pathname]);
}

/**
 * A new page starts at the top.
 *
 * Without this the browser keeps the scroll position across a client-side
 * navigation, and the front page is one long scroll on purpose — so reading to
 * the bottom of it and clicking "Catalogue" landed you most of the way down the
 * catalogue with its heading off-screen.
 *
 * Two things are deliberately left alone:
 *
 * `POP` — the back and forward buttons. Returning to a page you have already
 * read should put you back where you were, and the browser's own scroll
 * restoration does that better than we can.
 *
 * Anything carrying a hash. Those are requests for a *particular* part of a
 * page, and pages/Home.tsx already works to get them there against late-landing
 * webfonts. This effect would run afterwards — parent effects fire after their
 * children's — and undo it.
 */
function useScrollReset() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP" || hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navigationType]);
}

/**
 * `max-compact:py-[0.75rem]` is not cosmetic: it is what takes these links from
 * 27px tall to 44px on a phone. 27px clears the WCAG 2.2 AA target minimum of
 * 24px, but the site holds every button and every form control to 44px — these
 * were the most-tapped controls on it and the only ones missing that bar.
 *
 * It costs 18px of header, which is why the masthead stops being sticky at the
 * same breakpoint: 98px of permanently-held viewport is a lot of a phone. A
 * single-row mobile masthead would have avoided both, but the wordmark plus
 * three nav items need ~423px against the 337px a 375px screen leaves.
 */
const navLinkBase = "no-underline py-[0.4rem] border-b-2 max-compact:py-[0.75rem] hover:text-rust";
const navLinkActive = "text-ink border-b-rust";
const navLinkIdle = "text-muted border-transparent";

/**
 * The shell every page sits in.
 *
 * `main` deliberately holds no `.wrap`: pages are built from full-bleed bands
 * that alternate ground colour, and each band wraps its own contents.
 *
 * Three nav items, because a fourth wraps to a third row on a phone. "About"
 * is a section of the front page and lives in the footer instead — the line
 * page earns the slot, since that is where the promises a buyer cares about
 * are written down.
 *
 * Nothing in the masthead or the footer may acquire product vocabulary. A
 * visitor who has never seen a puzzle book has to be able to tell what this
 * company is.
 */
export default function Layout() {
  usePageMeta();
  useScrollReset();

  return (
    <div className="grain min-h-dvh flex flex-col">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="sticky top-0 z-10 bg-parchment grain max-compact:static">
        <Wrap className="flex items-center gap-4 min-h-[74px] flex-wrap max-compact:min-h-0 max-compact:pt-2 max-compact:pb-[0.35rem] max-compact:gap-[0.35rem]">
          <Link
            to="/"
            className="flex items-center gap-[0.65rem] no-underline text-ink mr-auto font-display font-bold text-[1.22rem] tracking-[0.02em] hover:text-rust max-compact:text-[1.05rem] max-compact:gap-2"
          >
            <img
              src={logoUrl}
              alt=""
              width={40}
              height={40}
              className="w-10 h-10 max-compact:w-[30px] max-compact:h-[30px] dark:[filter:invert(0.92)_sepia(0.25)]"
            />
            <span>
              Wise Hyena <span className="font-display font-normal italic text-muted">Press</span>
            </span>
          </Link>
          <nav
            className="flex gap-[clamp(0.9rem,3vw,1.7rem)] text-label uppercase tracking-[0.13em] font-bold max-compact:text-[0.7rem] max-compact:gap-[1.05rem]"
            aria-label="Main"
          >
            <NavLink
              to="/catalogue"
              className={({ isActive }) =>
                `${navLinkBase} ${isActive ? navLinkActive : navLinkIdle}`
              }
            >
              Catalogue
            </NavLink>
            <NavLink
              to="/logic-grids"
              className={({ isActive }) =>
                `${navLinkBase} ${isActive ? navLinkActive : navLinkIdle}`
              }
            >
              Logic grids
            </NavLink>
            <NavLink
              to="/help"
              className={({ isActive }) =>
                `${navLinkBase} ${isActive ? navLinkActive : navLinkIdle}`
              }
            >
              Help
            </NavLink>
          </nav>
        </Wrap>
        <hr className="block border-0 border-t border-line border-b-[3px] border-b-rust h-[3px] m-0" />
      </header>

      {/* tabIndex -1 so the skip link actually moves focus here, not just the
          scroll position — a bare id target is skipped by most browsers. */}
      {/*
        `isolate` keeps every z-index inside the page from reaching the header.
        A sticky row label in the grid asked for z-20, beat the header's z-10,
        and slid over the top of it. Fixing that one number would have left the
        next one to find; a stacking context here means page content can never
        paint over the site's own furniture, whatever it asks for.

        `overflow-x: clip`, not `hidden`. The puzzle grid escapes the page
        column by going 100vw wide, and 100vw counts the vertical scrollbar, so
        it lands a few pixels past the right edge of the document and gives the
        whole page a horizontal scrollbar. Clip trims those pixels. It has to be
        `clip` rather than `hidden` because `hidden` would make this a scroll
        container, which would break both the sticky header above and the sticky
        row labels inside the grid.
      */}
      <main id="main" tabIndex={-1} className="flex-1 outline-none overflow-x-clip isolate">
        <Outlet />
      </main>

      <footer className="border-t border-line text-muted text-[0.85rem] pt-[1.6rem] pb-[2.6rem]">
        <Wrap className="flex gap-4 flex-wrap items-center">
          <img
            className="w-7 h-7 opacity-55 dark:[filter:invert(0.92)_sepia(0.25)]"
            src={logoUrl}
            alt=""
            width={28}
            height={28}
          />
          <span>© {new Date().getFullYear()} Wise Hyena Press</span>
          <nav className="flex gap-4 flex-wrap" aria-label="Footer">
            <Link
              className="text-muted inline-flex items-center min-h-[44px] hover:text-rust"
              to="/#about"
            >
              About
            </Link>
            <a
              className="text-muted inline-flex items-center min-h-[44px] hover:text-rust"
              href="mailto:hello@wisehyena.com"
            >
              hello@wisehyena.com
            </a>
          </nav>
          <span className="flex-1" />
          <span>Checked before it goes to press.</span>
        </Wrap>
      </footer>
    </div>
  );
}
